import React, { PropsWithChildren, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { accountAccessShowLogin } from 'actions/UiActions'

import { useAppDispatch } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { NavWrapper } from 'components/OfferPage/styles'
import TextButton from 'LuxKit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import BodyText from 'LuxKit/Typography/BodyText'
import useBedbankRates from 'hooks/useBedbankRates'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import { getDisplayTotalPrice, getDisplayTotalPricePerNight } from 'lib/hotels/bedbankUtils'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { pluralizeToString } from 'lib/string/pluralize'
import config from 'constants/config'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'

const NavLayoutContainer = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const StyledPackageOptionsButton = styled(TextButton)`
  margin-left: auto;
`

const AuthWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  ${mediaQueryUp.tablet}{
    justify-content: flex-end;
  }

  & > * + * {
    margin-left: ${rem(16)};
  }
`

const ButtonsWrapper = styled.div`
  flex-shrink: 0;

  & > * + * {
    margin-left: ${rem(8)};
  }
`

const PriceSummarySection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: auto;
    padding-left: ${rem(12)};
    justify-content: flex-end;
    margin-left: auto;
  }

  > * + * {
    margin-left: ${rem(12)};
  }
`

const DesktopSignUp = styled(TextButton)`
  display: none;

  ${mediaQueryUp.tablet}{
    display: inline-flex;
  }
`

const MobileSignUp = styled(TextButton)`
  display: inline-flex;

  ${mediaQueryUp.tablet}{
    display: none;
  }
`

interface Props {
  duration: number;
  onViewPackageOptions?: () => void;
  offer: App.BedbankOffer;
  rooms: Array<App.Occupants>;
  checkInDate?: string;
  checkOutDate?: string;
  flightPrice?: number;
  flightsEnabled: boolean;
  luxPlusLPPEnabled: boolean;
}

function BedbankOfferHeader(props: PropsWithChildren<Props>) {
  const {
    children,
    onViewPackageOptions,
    offer,
    rooms,
    checkInDate,
    checkOutDate,
    duration,
    flightPrice,
    flightsEnabled,
    luxPlusLPPEnabled,
  } = props
  const dispatch = useAppDispatch()

  const ref = useRef<HTMLDivElement>(null)
  useHeaderStateObserver('bedbankOfferHeader', ref)
  const [{
    hotelOnlyRates,
    hotelBundleRates,
  }, fetching] = useBedbankRates(offer.id, rooms, checkInDate, checkOutDate)

  const sell = useBedbankOfferSell(offer, duration, rooms)

  const hotelPrice = hotelOnlyRates[0] ? hotelOnlyRates[0].totals.inclusive : sell?.price ?? 0
  const hotelMemberPrice = hotelOnlyRates[0] ? hotelOnlyRates[0].totals.memberInclusive ?? 0 : 0
  const bundlePrice = hotelBundleRates[0]?.totals.inclusive || hotelPrice
  const hotelNights = hotelOnlyRates[0]?.nights ?? sell?.los
  const propertyFees = hotelOnlyRates[0]?.totals?.propertyFees ?? sell?.propertyFees ?? 0
  const bundlePropertyFees = hotelBundleRates[0]?.totals.propertyFees || propertyFees

  const datesSelected = !!(checkInDate && checkOutDate)
  const hasRates = hotelOnlyRates.length > 0
  const showSummary = (datesSelected && hasRates) || (!datesSelected && sell)
  const isSoldOut = hotelOnlyRates.length == 0 && datesSelected
  const baseSaleUnit = 'room'
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : baseSaleUnit

  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle
  const showOneNightPrice = !offer.displayPricingAsPerPerson && !checkInDate && !!hotelNights && hotelNights > 1
  const nightsToDisplay = showOneNightPrice ? 1 : hotelNights

  const totalBasePrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    bundlePrice: bundlePrice + bundlePropertyFees,
    flightPrice,
    checkIn: checkInDate,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })

  const totalPrices = showOneNightPrice ? getDisplayTotalPricePerNight(totalBasePrices, hotelNights) : totalBasePrices
  const offerPrice = promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price
  const offerMemberPrice = promoteAsBundle ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice

  return (
    <NavWrapper>
      <NavLayoutContainer>
        {children}
        <WalledContent
          enforced={offer.walledGarden}
          hidePricing={offer.hidePricing}
          wall={(
            <AuthWrapper>
              <BodyText variant="medium" weight="bold">Sign up to access exclusive rates</BodyText>
              <ButtonsWrapper>
                <TextButton kind="secondary" onClick={() => dispatch(accountAccessShowLogin())}>
                  Log In
                </TextButton>
                <DesktopSignUp kind="primary" onClick={() => dispatch(accountAccessShowLogin())}>
                  Sign up for free
                </DesktopSignUp>
                <MobileSignUp kind="primary" onClick={() => dispatch(accountAccessShowLogin())}>
                  Sign up
                </MobileSignUp>
              </ButtonsWrapper>
            </AuthWrapper>
          )}
        >
          <PriceSummarySection>
            {showSummary && !isSoldOut && !fetching && <Group direction="vertical" tabletHorizontalAlign="end">
              <PriceRowPriceCaption>
                {pluralizeToString('night', nightsToDisplay)}{promoteAsBundle && ' + flights'} from
              </PriceRowPriceCaption>
              <LuxPlusPriceGuard
                showMemberPrice={(offerMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                fallbackPrice={<PriceRowPrice
                  data-testid="bedbank-sticky-header-price"
                  size="S"
                  price={offerPrice}
                  saleUnit={saleUnit}
                />}
              >
                <PriceRowLuxPlusPrice
                  size="S"
                  memberPrice={offerMemberPrice ?? 0}
                  saleUnit={saleUnit}
                  logoPlacement="leading"
                />
              </LuxPlusPriceGuard>
            </Group>}
            {isSoldOut && !fetching && <UrgencyLabel variant="critical2">SOLD OUT</UrgencyLabel>}
            <StyledPackageOptionsButton kind="primary" onClick={onViewPackageOptions}>
              <CSSBreakpoint max="tablet">View options</CSSBreakpoint>
              <CSSBreakpoint min="desktop">View {config.brandConfig.travelSynonym} options</CSSBreakpoint>
            </StyledPackageOptionsButton>
          </PriceSummarySection>
        </WalledContent>
      </NavLayoutContainer>
    </NavWrapper>
  )
}

const mapStateToProps = (state: App.State) => ({
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(state),
})

export default connect(mapStateToProps)(BedbankOfferHeader)
